<template>
    <main>
        <section class="title">
            <h4>{{ config.PROFILE_INFO_GENERAL_TITLE }}</h4>
            <router-link :to="{ name: 'profile.edit.general' }" :style="configStyles.primary.text">
                {{ config.PROFILE_EDIT }} <IconPen :size="24" :color="config.COLOR_PRIMARY"/>
            </router-link>
        </section>
        <section class="infos">
            <ul>
                <li>
                    <IconEmail class="icon" :color="config.COLOR_PRIMARY"/>
                    <div class="text">
                        <h5>{{ config.PROFILE_INFO_GENERAL_EMAIL }}</h5>
                        <span v-if="user.email">{{ user.email }}</span>
                        <span v-else>&mdash;</span>
                    </div>
                </li>
                <li>
                    <IconBirthday class="icon" :color="config.COLOR_PRIMARY"/>
                    <div class="text">
                        <h5>{{ config.PROFILE_INFO_GENERAL_BIRTHDATE }}</h5>
                        <span v-if="user.birthdate">{{ user.birthdate }}</span>
                        <span v-else>&mdash;</span>
                    </div>
                </li>
                <li>
                    <IconLocation class="icon" :color="config.COLOR_PRIMARY"/>
                    <div class="text">
                        <h5>{{ config.PROFILE_INFO_GENERAL_ADDRESS }}</h5>
                        <span v-if="user.addresses[0]">{{ user.addresses[0].formatted }}</span>
                        <span v-else>&mdash;</span>
                    </div>
                </li>
                <li>
                    <IconPhone class="icon" :color="config.COLOR_PRIMARY"/>
                    <div class="text">
                        <h5>{{ config.PROFILE_INFO_GENERAL_PHONE }}</h5>
                        <span v-if="user.phone">{{ user.phone }}</span>
                        <span v-else>&mdash;</span>
                    </div>
                </li>
            </ul>
        </section>
        <section class="break"/>
        <section class="title">
            <h4>{{ config.PROFILE_ADDRESSES_TITLE }}</h4>
            <router-link :to="{ name: 'profile.edit.address' }" :style="configStyles.primary.text">
                <span>{{ config.PROFILE_CREATE_ADDRESS }}</span>
                <IconPen :size="24" :color="config.COLOR_PRIMARY"/>
            </router-link>
        </section>
        <section class="infos">
            <ul v-if="user.addresses.length">
                <li v-for="address in user.addresses" :key="address.id">
                    <AddressItem
                        :address="address"
                        @onEdit="onAddressEdit"
                        @onDelete="onAddressDelete"
                    />
                </li>
            </ul>
            <p v-else>
                {{ config.PROFILE_ADDRESSES_EMPTY }}
            </p>
        </section>
        <section class="break"/>
        <section class="title">
            <h4>{{ config.PROFILE_INFO_SOCIAL_TITLE }}</h4>
            <router-link :to="{ name: 'profile.edit.social' }" :style="configStyles.primary.text">
                {{ config.PROFILE_EDIT }} <IconPen :size="24" :color="config.COLOR_PRIMARY"/>
            </router-link>
        </section>
        <section class="infos">
            <ul>
                <li>
                    <IconInstagramOutline class="icon" :color="config.COLOR_PRIMARY"/>
                    <div class="text">
                        <h5>{{ config.PROFILE_INFO_SOCIAL_INSTAGRAM }}</h5>
                        <span v-if="user.social.instagram">{{ user.social.instagram }}</span>
                        <span v-else>&mdash;</span>
                    </div>
                </li>
                <li>
                    <IconYoutubeOutline class="icon" :color="config.COLOR_PRIMARY"/>
                    <div class="text">
                        <h5>{{ config.PROFILE_INFO_SOCIAL_YOUTUBE }}</h5>
                        <span v-if="user.social.youtube">{{ user.social.youtube }}</span>
                        <span v-else>&mdash;</span>
                    </div>
                </li>
                <li>
                    <IconTwitterOutline class="icon" :color="config.COLOR_PRIMARY"/>
                    <div class="text">
                        <h5>{{ config.PROFILE_INFO_SOCIAL_TWITTER }}</h5>
                        <span v-if="user.social.twitter">{{ user.social.twitter }}</span>
                        <span v-else>&mdash;</span>
                    </div>
                </li>
                <li>
                    <IconYoutubeOutline class="icon" :color="config.COLOR_PRIMARY"/>
                    <div class="text">
                        <h5>{{ config.PROFILE_INFO_SOCIAL_SNAPCHAT }}</h5>
                        <span v-if="user.social.snapchat">{{ user.social.snapchat }}</span>
                        <span v-else>&mdash;</span>
                    </div>
                </li>
                <li>
                    <IconYoutubeOutline class="icon" :color="config.COLOR_PRIMARY"/>
                    <div class="text">
                        <h5>{{ config.PROFILE_INFO_SOCIAL_BLOG }}</h5>
                        <span v-if="user.social.website">{{ user.social.website }}</span>
                        <span v-else>&mdash;</span>
                    </div>
                </li>
            </ul>
        </section>
        <!-- Modals -->
        <transition name="fade">
            <ModalAddressDelete
                v-if="modals.deleteAddress"
                :address="address"
                @close="onAddressDeleteClose"
            />
        </transition>
    </main>
</template>
<script>
import { SolutionConsumer, UserConsumer } from '../../mixins';
import { ModalAddressDelete, AddressItem } from '../../components/shared';

export default {
    name: 'ProfileAbout',
    components: {
        AddressItem,
        ModalAddressDelete,
    },
    mixins: [SolutionConsumer, UserConsumer],
    data () {
        return {
            loading: false,
            address: {},
            modals: {
                deleteAddress: false,
            },
            privacy: false,
        };
    },
    methods: {
        onAddressEdit (address) {
            this.$router.push({
                name: 'profile.edit.address',
                params: { address },
                query: {
                    ...this.$route.query,
                },
            });
        },
        onAddressDelete (address) {
            this.modals.deleteAddress = true;
            this.address = address;
        },
        onAddressDeleteClose () {
            this.modals.deleteAddress = false;
            this.address = {};
        },
    },
};
</script>
<style lang="scss" scoped>
@import '../../assets/scss/mixins';
@import '../../assets/scss/colors';

main {
    @include container(20px);
    background-color: $white;

    > section {
        &.title {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap-reverse;

            h4 {
                text-transform: uppercase;
                color: $grey;
                margin: 0;
            }

            a {
                @include a-reset();
                display: inline-flex;
                align-items: center;
            }
        }

        &.infos {
            > ul {
                @include ul-reset();

                > li {
                    display: flex;

                    > .icon {
                        display: block;
                        flex: 0 0 32px;
                        margin-right: 10px;
                    }

                    > .text {
                        overflow: hidden;
                        text-overflow: ellipsis;
                        font-size: 14px;

                        > h5 {
                            color: $grey;
                            font-weight: normal;
                            font-size: inherit;
                        }
                    }
                }
            }
        }

        &.break {
            height: 1px;
            background-color: $grey-lightest;
        }
    }
}
</style>
